'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import * as React from 'react'
import {
  useSelect as dsUseSelect,
  UseSelectProps as DSUseSelectProps,
  UseSelectReturnValue as DSUseSelectReturnValue,
  UseSelectGetMenuPropsOptions,
  UseSelectGetToggleButtonPropsOptions,
  UseSelectGetItemPropsOptions
} from 'downshift'
import { noop } from 'lodash'

// lib
import { breakpoints } from '@theme'
import { BoxProps } from '../box'

/* -----------------------------------------------------------------------------
 * utils
 * -------------------------------------------------------------------------- */

// downshift's getters return DOM handlers; the kit's buttons and options take
// `onPress`. `onKeyPress` never reached the DOM, as with react-native-web.
const getNativeWebToggleButtonProps = (
  getDownshiftProps: (options?: UseSelectGetToggleButtonPropsOptions) => any,
  options?: UseSelectGetToggleButtonPropsOptions
) => {
  const { onClick, onKeyDown, ...rest } = getDownshiftProps(options)
  return { onPress: onClick, onKeyPress: onKeyDown, ...rest }
}

const getNativeWebItemProps = (
  getDownshiftProps: (options?: UseSelectGetItemPropsOptions<any>) => any,
  options?: UseSelectGetItemPropsOptions<any>
) => {
  const { onClick, ...rest } = getDownshiftProps(options)
  return { onPress: onClick, ...rest }
}

/* -----------------------------------------------------------------------------
 * useSelect
 * -------------------------------------------------------------------------- */

export type UseSelectProps<Item> = DSUseSelectProps<Item> & {
  buttonRef: React.MutableRefObject<any>
  menuRef: React.MutableRefObject<any>
}

export type UseSelectReturnValue<Item> = Omit<
  DSUseSelectReturnValue<Item>,
  'getMenuProps'
> & {
  getMenuProps: DSUseSelectReturnValue<Item>['getMenuProps'] & BoxProps
}

const useSelect = <Item>({
  buttonRef,
  menuRef,
  ...rest
}: UseSelectProps<Item>): UseSelectReturnValue<Item> => {
  // react-native-web's window dimensions
  const windowWidth = document.documentElement.clientWidth
  const windowHeight = document.documentElement.clientHeight
  const isMobile = windowWidth < breakpoints.tablet
  // the kit's refs hold the DOM nodes
  const buttonNode = buttonRef.current as Element | null
  const menuNode = menuRef.current as Element | null

  const [ignoreBlur, setIgnoreBlur] = React.useState<boolean>(true)
  const [{ height: menuHeight, width: menuWidth }, setMenuDimensions] =
    React.useState<{
      height: number
      width: number
    }>({ height: 0, width: 0 })

  const {
    isOpen,
    getToggleButtonProps: dsGetToggleButtonProps,
    getItemProps: dsGetItemProps,
    getMenuProps: dsGetMenuProps,
    closeMenu,
    ...ret
  } = dsUseSelect({
    // Downshift immediately triggers a `menuBlur` event after the toggle
    // button is clicked in mobile contexts. We need to ignore this first
    // blur event but respond to the next event, which will be the user
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges
      const res =
        type === '__menu_blur__' && ignoreBlur && isMobile
          ? { ...changes, isOpen: true }
          : changes

      if (type === '__menu_blur__') {
        setIgnoreBlur(!ignoreBlur)
      }
      return res
    },
    scrollIntoView: noop,
    ...rest
  })

  // To position the menu properly across all screen sizes, we need to know the menu's dimensions
  // as soon as it's been opened.
  React.useEffect(() => {
    if (menuNode && (menuNode.clientHeight || menuNode.clientWidth)) {
      setMenuDimensions({
        height: menuNode.clientHeight || menuHeight,
        width: menuNode.clientWidth || menuWidth
      })
    }
  }, [isOpen])

  const getTabletTop = () => {
    if (!buttonNode) {
      return 0
    }

    const buttonBottom = buttonNode.getBoundingClientRect().bottom

    if (buttonBottom + menuHeight > windowHeight) {
      return windowHeight - menuHeight
    }
    return buttonBottom
  }
  const getTabletLeft = () => {
    if (!buttonNode) {
      return 0
    }

    const buttonLeft = buttonNode.getBoundingClientRect().left

    if (buttonLeft + menuWidth > windowWidth) {
      return windowWidth - menuWidth
    }
    return buttonLeft
  }

  const getToggleButtonProps = (
    options: UseSelectGetToggleButtonPropsOptions
  ) => getNativeWebToggleButtonProps(dsGetToggleButtonProps, options)

  const getItemProps = (options: UseSelectGetItemPropsOptions<Item>) =>
    getNativeWebItemProps(dsGetItemProps, options)

  const getMenuProps = (options: UseSelectGetMenuPropsOptions) => ({
    ...dsGetMenuProps({ ref: menuRef, ...options }),
    display: isOpen ? 'block' : 'none',
    left: { mobile: 0, tablet: getTabletLeft() },
    top: { mobile: 0, tablet: getTabletTop() }
  })

  return {
    isOpen,
    closeMenu,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    ...ret
  }
}

export default useSelect
